import type { NotificationChannel, NotificationDelivery } from '@stage/contracts';
import { newRequestId, parseError } from './client.js';

/**
 * Integrations / Notifications API client (M4, §21): configure an ntfy notification channel, send a
 * test notification, and inspect delivery health. Shares the {@link ApiError}/`ErrorEnvelope`
 * contract and bearer-token pattern of {@link createAdminClient}.
 *
 * §21.2 security: the ntfy topic and token/credentials are **write-only inputs**. They are sent on
 * create/update and are NEVER returned — a {@link NotificationChannel} exposes only `secretRef` (an
 * opaque id) and `safeConfig` (host + topic fingerprint). Callers must never store the secret fields
 * after submit, and never render a topic/token anywhere.
 */

const BASE = '/v1';

/**
 * Write-only ntfy connection inputs (§21.1). `topic`, `token`, `username`, `password` are secrets:
 * sent on create/update, never echoed back.
 */
export interface NtfyConfigInput {
  baseUrl: string;
  topic: string;
  token?: string;
  username?: string;
  password?: string;
}

/** Body for `POST /v1/artists/{artistId}/notification-channels`. */
export interface CreateChannelInput {
  name: string;
  provider: 'ntfy';
  ntfy: NtfyConfigInput;
  eventPolicy?: Record<string, boolean>;
}

/** Body for `PATCH /v1/notification-channels/{channelId}`. Send only what changed. */
export interface UpdateChannelInput {
  name?: string;
  eventPolicy?: Record<string, boolean>;
  status?: 'ready' | 'disabled';
  ntfy?: Pick<NtfyConfigInput, 'baseUrl' | 'topic' | 'token'>;
}

/** Compact result of a test/replay action (§21.4): a delivery id and its current status. */
export interface DeliveryActionResult {
  deliveryId: string;
  status: string;
}

/** Query filters for `GET /v1/notification-deliveries`. */
export interface DeliveryQuery {
  channelId?: string;
  gigId?: string;
}

export interface NotificationsClient {
  listChannels(artistId: string): Promise<NotificationChannel[]>;
  createChannel(artistId: string, input: CreateChannelInput): Promise<NotificationChannel>;
  updateChannel(channelId: string, input: UpdateChannelInput): Promise<NotificationChannel>;
  testChannel(channelId: string): Promise<DeliveryActionResult>;
  listDeliveries(query?: DeliveryQuery): Promise<NotificationDelivery[]>;
  replayDelivery(deliveryId: string): Promise<DeliveryActionResult>;
}

/**
 * Create a notifications client bound to a live access-token getter, mirroring
 * {@link createAdminClient}. The getter is read per request so silent refresh is transparent (§32.3).
 */
export function createNotificationsClient(
  getAccessToken: () => string | null,
): NotificationsClient {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    headers.set('x-request-id', newRequestId());
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const enc = encodeURIComponent;

  return {
    async listChannels(artistId) {
      const body = await request<{ items: NotificationChannel[] }>(
        `/artists/${enc(artistId)}/notification-channels`,
      );
      return body.items;
    },
    createChannel(artistId, input) {
      return request<NotificationChannel>(`/artists/${enc(artistId)}/notification-channels`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    updateChannel(channelId, input) {
      return request<NotificationChannel>(`/notification-channels/${enc(channelId)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
    },
    testChannel(channelId) {
      return request<DeliveryActionResult>(`/notification-channels/${enc(channelId)}/test`, {
        method: 'POST',
      });
    },
    async listDeliveries(query) {
      const qs = new URLSearchParams();
      if (query?.channelId) qs.set('channelId', query.channelId);
      if (query?.gigId) qs.set('gigId', query.gigId);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const body = await request<{ items: NotificationDelivery[] }>(
        `/notification-deliveries${suffix}`,
      );
      return body.items;
    },
    replayDelivery(deliveryId) {
      return request<DeliveryActionResult>(
        `/notification-deliveries/${enc(deliveryId)}/replay`,
        { method: 'POST' },
      );
    },
  };
}
